<template>
    <BaseCard padding="lg" class="h-full new-home-card" :style="backgroundStyles">
        <div class="flex flex-col gap-4 relative h-full" :class="{ 'rounded-lgr p-6 bg-surface-raised/60 backdrop-blur-[20px]': backdrop }">
            <div v-if="icon" class="pb-6" aria-hidden="true">
                <component :is="icon" />
            </div>
            <div v-if="$slots.header"><slot name="header"></slot></div>
            <div>
                <slot name="subtitle">
                    <div class="body-small text-ink-muted">{{ subtitle }}</div>
                </slot>
                <slot name="title">
                    <div class="title-body wrap-break-word text-ink">{{ title }}</div>
                </slot>
            </div>
            <div v-if="content">
                <p ref="contentRef" class="body fs-mask text-ink-muted" :class="{ 'line-clamp-4': !expanded }">
                    {{ content }}
                </p>
                <BaseButton v-if="isTruncated" theme="tertiary" size="sm" class="mt-1" :aria-expanded="expanded" @click="expanded = !expanded">
                    {{ expanded ? t("chat.home.card.showLess") : t("chat.home.card.showMore") }}
                </BaseButton>
            </div>
            <slot v-if="$slots.content" name="content"></slot>
            <div v-if="ctaButtonText || $slots.cta" class="mt-auto">
                <slot name="cta">
                    <BaseButton v-if="ctaButtonText" theme="neutral" class="w-full" :style="ctaButtonStyle" @click="$emit('cta-click')">
                        {{ ctaButtonText }}
                    </BaseButton>
                </slot>
            </div>
            <slot name="footer"></slot>
        </div>
    </BaseCard>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { cardType, backgroundStyle, title, analyticsTitle, content } = defineProps({
    cardType: { type: String, required: true },
    title: { type: String, default: undefined },
    // English title for analytics when `title` is translated copy, so events keep one value across languages.
    analyticsTitle: { type: String, default: undefined },
    subtitle: { type: String, default: undefined },
    content: { type: String, default: undefined },
    ctaButtonText: { type: String, default: undefined },
    backgroundStyle: { type: Object, default: undefined },
    backdrop: { type: Boolean, default: false }, // Whether to show the backdrop
    ctaButtonStyle: { type: Object, default: () => ({}) },
    icon: { type: [Object, Function, String], default: null }, // Accepts component or path
});

defineEmits(["cta-click"]);

const { t } = useI18n();

const backgroundStyles = computed(() => {
    if (backgroundStyle) {
        return backgroundStyle;
    }
    return {};
});

const contentRef = ref(null);
const expanded = ref(false);
const isTruncated = ref(false);
let resizeObserver = null;

const measureTruncation = () => {
    if (!contentRef.value || expanded.value) {
        return;
    }
    const el = contentRef.value;
    isTruncated.value = el.scrollHeight > el.clientHeight + 1;
};

onMounted(async () => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_SHOWN, { card_type: cardType, title: analyticsTitle ?? title });
    await nextTick();
    measureTruncation();
    if (typeof ResizeObserver !== "undefined" && contentRef.value) {
        resizeObserver = new ResizeObserver(() => measureTruncation());
        resizeObserver.observe(contentRef.value);
    }
});

onUnmounted(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
});

watch(
    () => content,
    async () => {
        await nextTick();
        measureTruncation();
    },
);
</script>
